import { LocationType } from '@app/enums';
import { environment } from '@app/environments/environment';
import { LocationDetailsModel } from '@app/models';
import { AutocompleteItemSCModel, EsResponseManyModel } from '@app/models/elastic-search';
import { EsServiceBase } from '@app/services/abstracts/es-service.base';

export class EsAutocompleteService extends EsServiceBase {
    readonly #autocompleteEsIndexName = 'noc-autocomplete';
    readonly #autocompleteEsIndexVersion = environment.AUTOCOMPLETE_ES_VERSION;

    private static _instance: EsAutocompleteService;

    protected indexName(version?: number): string {
        return `${this.#autocompleteEsIndexName}-${version ?? this.#autocompleteEsIndexVersion}`;
    }

    public static get instance(): EsAutocompleteService {
        if (!this._instance) {
            this._instance = new EsAutocompleteService();
        }

        return this._instance;
    }

    private buildQuery(query: string) {
        return {
            function_score: {
                query: {
                    bool: {
                        should: [
                            {
                                multi_match: {
                                    query,
                                    type: 'bool_prefix',
                                    fields: [
                                        'name.autocomplete',
                                        'name.autocomplete._2gram',
                                        'name.autocomplete._3gram',
                                    ],
                                    operator: 'and',
                                    boost: 100,
                                },
                            },
                            {
                                match: {
                                    'name.autocomplete': {
                                        query,
                                        fuzziness: 'AUTO:3,6',
                                        operator: 'and',
                                        prefix_length: 1,
                                        boost: 0.001,
                                    },
                                },
                            },
                        ],
                        minimum_should_match: 1,
                    },
                },
                functions: [
                    {
                        filter: {
                            terms: {
                                type: ['city', 'country', 'region', 'voivodeship'],
                            },
                        },
                        script_score: {
                            script: '(!doc["details.popularity"].empty ? (doc["details.popularity"].value * 0.01) : 1) + (doc["details.accommodations_count"].value * 0.01)',
                        },
                        weight: 2,
                    },
                    {
                        filter: {
                            term: {
                                type: 'poi',
                            },
                        },
                        field_value_factor: {
                            field: 'details.accommodations_count',
                            factor: 0.01,
                        },
                        weight: 0.1,
                    },
                    {
                        filter: {
                            term: {
                                type: 'accommodation',
                            },
                        },
                        script_score: {
                            script: '(doc["details.ota"].value ? 20 : 0) + (doc["details.rating"].value) + (doc["details.rating_count"].value * 0.01)',
                        },
                        weight: 0.01,
                    },
                ],
                boost_mode: 'multiply',
            },
        };
    }

    public async loadResults(
        query: string,
        size = 5,
        indexVersion?: number,
    ): Promise<EsResponseManyModel<AutocompleteItemSCModel>> {
        return await this.getMany(this.indexName(indexVersion), {
            size,
            _source: ['id', 'name', 'caption', 'path', 'type', 'details'],
            query: this.buildQuery(query),
        });
    }

    public async getLocationDetails(
        geoId: string,
        locationType: LocationType,
        indexVersion?: number,
    ): Promise<LocationDetailsModel | null> {
        const response = await this.getMany<AutocompleteItemSCModel>(this.indexName(indexVersion), {
            size: 1,
            _source: [
                'name',
                'type',
                'path',
                'details.location',
                'details.radius',
                'details.region_id',
                'details.accommodation_id',
            ],
            query: {
                match: {
                    id: `${locationType}/${geoId}`,
                },
            },
        });

        const hit = response.hits?.[0];
        if (!hit?._source) {
            return null;
        }

        const {
            location = { lon: 0, lat: 0 },
            radius = 0,
            region_id,
            accommodation_id,
        } = hit._source.details ?? {};

        return {
            name: hit._source.name,
            type: hit._source.type,
            path: hit._source.path,
            details: {
                location,
                radius,
                regionId: region_id,
                accommodationId: accommodation_id,
            },
        };
    }
}
